import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const handleTokenExpiration = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    navigate('/login');
  }, [navigate]);

  // Add axios interceptor for handling token expiration
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          handleTokenExpiration();
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [handleTokenExpiration]);

  useEffect(() => {
    
    const token = localStorage.getItem('token');
    if (token) {
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Decode token to get role and user ID
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        // Check if token has required fields
        if (!payload.id || !payload.role) {
          // console.error('Token missing required fields:', payload);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          return;
        }
        
        // Check if token is expired
        if (payload.exp && payload.exp < Date.now() / 1000) {
          // console.error('Token expired');
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          return;
        }
        
        setUser({ 
          id: payload.id, 
          role: payload.role 
        });
        // Only redirect if we're on login path
        if (location.pathname === '/login') {
          navigate('/events');
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        console.error('Error type:', error.constructor.name);
        // Don't remove token on decoding errors - it might be a temporary issue
        // Only remove if we can't parse it at all
        if (error instanceof SyntaxError) {
          console.error('Malformed token, removing from storage');
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
    } else {
      // No token found, redirect to login if not already there and not on public paths
      if (location.pathname !== '/login' && location.pathname !== '/register' && location.pathname !== '/') {
        navigate('/login');
      }
    }
    setLoading(false);
  }, [navigate, location]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({ 
        id: user.id, 
        role: user.role 
      });
      
      // Navigate to event selection after successful login
      navigate('/events');
      
      return { success: true, role: user.role };
    } catch (error) {
      console.error('Login failed:', error.response?.data?.error || error.message);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    navigate('/');
  };



  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 